import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle2,
  Eye,
  LayoutGrid,
  List as ListIcon,
  Loader2,
  Phone,
  PlayCircle,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Permit } from '@/components/guards/Permit'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_FILTER_HOVER_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { MultiSelectFilter } from '@/components/ui/multi-select-filter'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { ListPagination } from '@/components/shared/ListPagination'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import {
  TaskStatusBadge,
  TaskPriorityBadge,
} from '@/components/shared/StatusBadges'
import { DateRangeFilter } from '@/components/clients/tabs/DateRangeFilter'
import { DeleteTaskDialog } from '@/components/tasks/DeleteTaskDialog'
import TaskDetailDrawerContent from '@/components/tasks/TaskDetailDrawerContent'
import { UpdateTaskStatusDialog } from '@/components/tasks/UpdateTaskStatusDialog'
import { PaymentDrawerShell } from '@/components/payments/PaymentDrawerShell'
import TaskFormPage from '@/pages/tasks/TaskFormPage'
import {
  useInfiniteTasksList,
  useTasksList,
  useTasksSummary,
} from '@/hooks/useTasks'
import { useUrlListParams } from '@/hooks/useUrlListParams'
import { useTopBarActions } from '@/contexts/TopBarActionsContext'
import { callClient, whatsappClient } from '@/lib/integrations'
import {
  KANBAN_COLUMN_BATCH_SIZE,
  shouldLoadMoreKanbanTasks,
  type KanbanColumnKey,
} from '@/lib/kanbanLazyLoad'
import { canUpdateTaskStatus } from '@/lib/tasks'
import { toast } from '@/components/ui/toast'
import { daysUntil, formatDate, formatInteger } from '@/lib/format'
import { useAppSelector } from '@/store/hooks'
import {
  TASK_RELATED_TYPES,
  normalizeRelatedType,
  type Task,
  type TaskListQuery,
  type TaskStatus,
} from '@/types'
import whatsappLineIcon from '@/assets/clients/actions/whatsapp-line.svg'

const ALL = '__all__'

const topBarNewTaskClass =
  'h-10 gap-2 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 py-3 text-sm font-medium leading-none text-white hover:bg-[#e6511c]'

const STATUS_OPTIONS: { value: TaskStatus; labelKey: string }[] = [
  { value: 'todo', labelKey: 'statusOptions.todo' },
  { value: 'in_progress', labelKey: 'statusOptions.in_progress' },
  { value: 'done', labelKey: 'statusOptions.done' },
  { value: 'cancelled', labelKey: 'statusOptions.cancelled' },
]

const PRIORITY_OPTIONS = [
  { value: 'low', labelKey: 'priorityOptions.low' },
  { value: 'medium', labelKey: 'priorityOptions.medium' },
  { value: 'high', labelKey: 'priorityOptions.high' },
  { value: 'urgent', labelKey: 'priorityOptions.urgent' },
]

type KanbanStatus = KanbanColumnKey

const KANBAN_COLUMNS: { status: KanbanStatus; labelKey: string }[] = [
  { status: 'todo', labelKey: 'statusOptions.todo' },
  { status: 'in_progress', labelKey: 'statusOptions.in_progress' },
  { status: 'done', labelKey: 'statusOptions.done' },
]

function fromCsv(value: string | undefined): TaskStatus[] {
  return value ? (value.split(',').filter(Boolean) as TaskStatus[]) : []
}

function toCsv(values: string[]): string | undefined {
  return values.length ? values.join(',') : undefined
}

// Card view column accents (Figma 8:1294): dot + light count badge only.
const KANBAN_COLUMN_THEMES = {
  todo: {
    dot: '#2550CC',
    label: '#2550CC',
    countBg: '#E7E9EF',
    countText: '#171A20',
  },
  in_progress: {
    dot: '#CE7E0A',
    label: '#CE7E0A',
    countBg: '#E7E9EF',
    countText: '#171A20',
  },
  done: {
    dot: '#059669',
    label: '#059669',
    countBg: '#E7E9EF',
    countText: '#171A20',
  },
} as const

// Summary card themes (Figma 1360:51378): compact tiles with per-metric
// background tint, border, and icon colour.
const CARD_THEMES = {
  todo: {
    border: '#c0d4f5',
    tint: '#f1f6fd',
    label: '#3271d7',
    value: '#272c36',
  },
  inProgress: {
    border: '#ffdc80',
    tint: '#fff8e6',
    label: '#b38100',
    value: '#272c36',
  },
  completed: {
    border: '#aff4c6',
    tint: '#ebffee',
    label: '#009951',
    value: '#272c36',
  },
  late: {
    border: '#f8b4b4',
    tint: '#fde9e8',
    label: '#d63b33',
    value: '#d63b33',
  },
} as const

// Toolbar filter trigger styling (Figma 8:1130): 40px tall, rounded-12,
// #d9d9d9 border, 14px regular text — shared with other restyled list modules.
const filterTriggerClass =
  'h-10 w-auto shrink-0 min-w-[120px] justify-between gap-2 rounded-xl border-input bg-white py-3 pl-4 pr-3 text-sm font-normal focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:ring-0 [&_svg]:size-4'

// Table styling (Figma 8:1148): 40px header, 64px rows, subtle zebra striping.
const thClass =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-[10px] font-medium uppercase tracking-normal text-[#3d434e]'

const tdClass =
  'h-16 overflow-hidden border-b border-[#e7e9ef] px-3 py-2 align-middle text-sm'

const actionBtnClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] text-[#272c36] group-hover:bg-[#fbfbfc] hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] group-hover:hover:bg-[#fff1ea]'

const whatsappActionBtnClass =
  'group/whatsapp hover:border-[#10b981] hover:bg-[#ecfdf5] hover:text-[#10b981] group-hover:hover:bg-[#ecfdf5]'

const destructiveActionBtnClass =
  'hover:border-[#d63b33] hover:bg-[#fde9e8] hover:text-[#d63b33] group-hover:hover:bg-[#fde9e8]'

const overdueBadgeClass =
  'inline-flex items-center justify-center rounded-lg bg-[#FECACA] px-1.5 py-1 text-xs font-medium leading-4 text-[#7F1D1D]'

const kanbanOverdueTextClass = 'text-xs font-medium leading-4 text-[#b91c1c]'

const actionIconClass = 'size-4 shrink-0'
const actionClusterClass = 'ml-auto flex w-[176px] items-center gap-1'

const cardActionBtnClass =
  'inline-flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-transparent bg-[#f5f6fa] text-[#272c36] transition-colors hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'

const cardWhatsappActionBtnClass =
  'group/card-whatsapp hover:border-[#10b981] hover:bg-[#ecfdf5] hover:text-[#10b981]'

const cardDestructiveActionBtnClass =
  'hover:border-[#d63b33] hover:bg-[#fde9e8] hover:text-[#d63b33]'

const cardActionIconClass = 'h-4 w-4 shrink-0'

const PRIORITY_ACCENT = {
  low: '#303030',
  medium: '#3f81ea',
  high: '#efac00',
  urgent: '#d63b33',
} as const

/** Figma 8:1148 column widths (6 visible columns, min width 1152px). */
const COL = {
  taskTitle: 'w-[392px] min-w-[392px]',
  priority: 'w-[120px] min-w-[120px]',
  dueDate: 'w-[160px] min-w-[160px]',
  status: 'w-[160px] min-w-[160px] text-center',
  assignedTo: 'w-[160px] min-w-[160px]',
  actions: 'w-[200px] min-w-[200px]  text-right',
} as const

const TABLE_MIN_WIDTH = 1180
const COLUMN_COUNT = 6

type TaskView = 'list' | 'card'

/** Navigation state so task detail can `navigate(-1)` and restore list URL (view, filters). */
const TASKS_LIST_NAV_STATE = { fromTasksList: true } as const

type Filters = {
  status?: string
  priority?: string
  due_date_from?: string
  due_date_to?: string
  overdue?: string
}

type TaskCardKey = 'todo' | 'inProgress' | 'completed' | 'late'

function useTaskKanbanColumn(
  baseQuery: TaskListQuery,
  selectedStatuses: TaskStatus[],
  status: KanbanStatus,
  enabled: boolean
) {
  const statusEnabled =
    selectedStatuses.length === 0 || selectedStatuses.includes(status)
  const columnQuery = useMemo(
    (): TaskListQuery => ({ ...baseQuery, status }),
    [baseQuery, status]
  )

  return useInfiniteTasksList(columnQuery, {
    enabled: enabled && statusEnabled,
    pageSize: KANBAN_COLUMN_BATCH_SIZE,
  })
}

// 2.9.1 — Task list. KPI cards, search/filter, List + Card (kanban) toggle,
// overdue highlighting, quick actions, pagination via the shared control.
export default function TasksListPage() {
  const { t } = useTranslation('tasks')
  const navigate = useNavigate()
  const detailMatch = useMatch('/tasks/:id')
  const editMatch = useMatch('/tasks/:id/edit')
  const [searchParams, setSearchParams] = useSearchParams()
  const view: TaskView = searchParams.get('view') === 'card' ? 'card' : 'list'
  const activePanel = searchParams.get('panel')
  const detailTaskId = editMatch ? null : detailMatch?.params.id
  const editingTaskId = editMatch?.params.id
  const drawerMode: 'detail' | 'create' | 'edit' | null = editingTaskId
    ? 'edit'
    : detailTaskId
      ? 'detail'
      : activePanel === 'create'
        ? 'create'
        : null

  useEffect(() => {
    if (
      searchParams.get('view') === 'card' &&
      searchParams.get('due_date_from') === '2026-07-15' &&
      searchParams.get('due_date_to') === '2026-08-13'
    ) {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          next.delete('due_date_from')
          next.delete('due_date_to')
          return next
        },
        { replace: true }
      )
    }
  }, [searchParams, setSearchParams])

  const setView = useCallback(
    (next: TaskView) => {
      setSearchParams(
        prev => {
          const nextParams = new URLSearchParams(prev)
          nextParams.delete('page')
          nextParams.delete('limit')
          if (next === 'list') {
            nextParams.delete('view')
            nextParams.delete('due_date_from')
            nextParams.delete('due_date_to')
          } else {
            nextParams.set('view', 'card')
          }
          return nextParams
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )
  const openTaskDetail = useCallback(
    (taskId: string) => {
      const qs = searchParams.toString()
      navigate(
        {
          pathname: `/tasks/${taskId}`,
          search: qs ? `?${qs}` : '',
        },
        { state: TASKS_LIST_NAV_STATE }
      )
    },
    [navigate, searchParams]
  )
  const openCreatePanel = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.set('panel', 'create')
        return next
      },
      { replace: false }
    )
  }, [setSearchParams])
  const closePanel = useCallback(() => {
    const qs = searchParams.toString()

    if (editingTaskId) {
      navigate(
        {
          pathname: `/tasks/${editingTaskId}`,
          search: qs ? `?${qs}` : '',
        },
        { replace: false }
      )
      return
    }

    if (detailTaskId) {
      navigate(
        {
          pathname: '/tasks',
          search: qs ? `?${qs}` : '',
        },
        { replace: false }
      )
      return
    }

    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.delete('panel')
        return next
      },
      { replace: false }
    )
  }, [detailTaskId, editingTaskId, navigate, searchParams, setSearchParams])
  const [toDelete, setToDelete] = useState<Task | null>(null)
  const [statusTask, setStatusTask] = useState<Task | null>(null)
  const viewer = useAppSelector(s => s.auth.user)
  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  } = useUrlListParams<Filters>({})

  const selectedStatuses = useMemo(
    () => fromCsv(filters.status),
    [filters.status]
  )
  const cardBaseQuery = useMemo(
    (): TaskListQuery => ({
      search: query.search,
      priority: filters.priority as TaskListQuery['priority'] | undefined,
      due_date_from: filters.due_date_from,
      due_date_to: filters.due_date_to,
      overdue: filters.overdue === '1' ? '1' : undefined,
    }),
    [
      query.search,
      filters.priority,
      filters.due_date_from,
      filters.due_date_to,
      filters.overdue,
    ]
  )
  const effectiveQuery = useMemo((): TaskListQuery => {
    if (view === 'card') {
      return {
        ...cardBaseQuery,
        status: selectedStatuses.length ? selectedStatuses : undefined,
      }
    }
    const { status: _status, ...listQuery } = query
    void _status
    return {
      ...listQuery,
      status: selectedStatuses.length ? selectedStatuses : undefined,
      overdue: filters.overdue === '1' ? '1' : undefined,
    } as TaskListQuery
  }, [view, query, cardBaseQuery, selectedStatuses, filters.overdue])

  const { data, isLoading, isError } = useTasksList(effectiveQuery, {
    enabled: view === 'list',
    paginate: true,
  })
  const tasks = useMemo(() => data?.data ?? [], [data])
  const todoTasks = useTaskKanbanColumn(
    cardBaseQuery,
    selectedStatuses,
    'todo',
    view === 'card'
  )
  const inProgressTasks = useTaskKanbanColumn(
    cardBaseQuery,
    selectedStatuses,
    'in_progress',
    view === 'card'
  )
  const doneTasks = useTaskKanbanColumn(
    cardBaseQuery,
    selectedStatuses,
    'done',
    view === 'card'
  )
  const { data: summaryData } = useTasksSummary()
  const summary = summaryData?.data
  const kanbanQueries = useMemo(
    () => ({
      todo: todoTasks,
      in_progress: inProgressTasks,
      done: doneTasks,
    }),
    [doneTasks, inProgressTasks, todoTasks]
  )

  // Summary KPI cards (2.9.1-003).
  const cards = useMemo(() => {
    return {
      todo: {
        total: summary?.todo ?? 0,
        helperCount: summary?.todo_due_today ?? 0,
      },
      inProgress: {
        total: summary?.in_progress ?? 0,
        helperCount: summary?.in_progress_assignee_count ?? 0,
      },
      completed: {
        total: summary?.completed ?? 0,
        helperCount: summary?.completed_this_week ?? 0,
      },
      late: {
        total: summary?.late ?? 0,
      },
    }
  }, [summary])

  const activeCardKey = useMemo((): TaskCardKey | undefined => {
    const hasDateRange = !!filters.due_date_from || !!filters.due_date_to
    const overdueOnly = filters.overdue === '1'
    if (filters.status === 'todo' && !hasDateRange && !overdueOnly)
      return 'todo'
    if (filters.status === 'in_progress' && !hasDateRange && !overdueOnly)
      return 'inProgress'
    if (filters.status === 'done' && !hasDateRange && !overdueOnly)
      return 'completed'
    if (!filters.status && overdueOnly) return 'late'
    return undefined
  }, [filters])

  const applyCardFilter = useCallback(
    (nextFilter: {
      status?: TaskStatus
      due_date_from?: string
      due_date_to?: string
      overdue?: string
    }) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          next.delete('page')
          next.delete('view')
          for (const key of [
            'status',
            'due_date_from',
            'due_date_to',
            'overdue',
          ]) {
            next.delete(key)
          }
          for (const [key, value] of Object.entries(nextFilter)) {
            if (value) next.set(key, value)
          }
          return next
        },
        { replace: false }
      )
    },
    [setSearchParams]
  )

  const isOverdue = (t: Task) => {
    if (t.status === 'done' || t.status === 'cancelled') return false
    const d = daysUntil(t.due_date)
    return d !== null && d < 0
  }

  const linkedEntity = (t: Task) => {
    if (!t.related_type || !t.related_id) return null
    const meta = TASK_RELATED_TYPES.find(
      r => r.value === normalizeRelatedType(t.related_type)
    )
    if (!meta) return null
    const to =
      meta.value === 'job'
        ? `/calendar?job=${t.related_id}`
        : `${meta.path}/${t.related_id}`
    return { label: meta.label, to }
  }

  // Quick actions (feedback #68): dial / WhatsApp the assignee straight from the
  // row. Falls back to a destructive toast when the assignee has no phone.
  const onCall = (task: Task) => {
    const res = callClient(task.assignee_phone)
    if (!res.ok) {
      toast({ variant: 'destructive', title: t('list.noPhone') })
    }
  }
  const onWhatsapp = (task: Task) => {
    const res = whatsappClient(task.assignee_phone)
    if (!res.ok) {
      toast({ variant: 'destructive', title: t('list.noPhone') })
    }
  }

  const topBarActions = useMemo(
    () => (
      <Permit module='tasks' action='edit'>
        <Button className={topBarNewTaskClass} onClick={openCreatePanel}>
          <Plus className='size-4' />
          {t('list.createButton')}
        </Button>
      </Permit>
    ),
    [openCreatePanel, t]
  )
  useTopBarActions(topBarActions)

  return (
    <div
      className={cn(
        '-m-6 flex min-h-full flex-col gap-6 bg-[#F4F5F8] p-6',
        view === 'card' && 'h-[calc(100dvh-9.75rem)] overflow-hidden'
      )}
    >
      <section className='grid shrink-0 gap-4 sm:grid-cols-2 xl:grid-cols-4'>
        {[
          {
            key: 'todo' as const,
            labelKey: 'list.cards.todo',
            value: cards.todo.total,
            helperCount: cards.todo.helperCount,
            helperKey: 'list.cards.todoHelper',
            theme: CARD_THEMES.todo,
            icon: ListIcon,
            onClick: () => applyCardFilter({ status: 'todo' }),
          },
          {
            key: 'inProgress' as const,
            labelKey: 'list.cards.inProgress',
            value: cards.inProgress.total,
            helperCount: cards.inProgress.helperCount,
            helperKey: 'list.cards.inProgressHelper',
            theme: CARD_THEMES.inProgress,
            icon: PlayCircle,
            onClick: () => applyCardFilter({ status: 'in_progress' }),
          },
          {
            key: 'completed' as const,
            labelKey: 'list.cards.completed',
            value: cards.completed.total,
            helperCount: cards.completed.helperCount,
            helperKey: 'list.cards.completedHelper',
            theme: CARD_THEMES.completed,
            icon: CheckCircle2,
            onClick: () => applyCardFilter({ status: 'done' }),
          },
          {
            key: 'late' as const,
            labelKey: 'list.cards.late',
            value: cards.late.total,
            helperKey: 'list.cards.lateHelper',
            theme: CARD_THEMES.late,
            icon: AlertCircle,
            onClick: () => applyCardFilter({ overdue: '1' }),
          },
        ].map(c => (
          <button
            key={c.key}
            type='button'
            aria-pressed={activeCardKey === c.key}
            onClick={c.onClick}
            className='min-w-[220px] rounded-2xl border px-4 py-4 text-left shadow-[0_1px_4px_-1px_rgba(38,45,66,0.05),0_1px_8px_-1px_rgba(38,45,66,0.1)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-12px_rgba(23,26,32,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            style={{
              borderColor: activeCardKey === c.key ? '#ff5a1f' : c.theme.border,
              backgroundColor: c.theme.tint,
            }}
          >
            <div className='flex items-center gap-4'>
              <div
                className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/80 bg-white shadow-[0_1px_4px_rgba(38,45,66,0.05)]'
                style={{ color: c.theme.label }}
              >
                <c.icon className='h-4 w-4' />
              </div>
              <div className='min-w-0 flex-1 space-y-1'>
                <p className='font-heading text-xs font-medium leading-[1.2] text-[#5a5a5a] mb-1.5'>
                  {t(c.labelKey)}
                </p>
                <p
                  className='font-heading text-[24px] font-semibold leading-none tracking-[-0.04em]'
                  style={{ color: c.theme.value }}
                >
                  {formatInteger(c.value)}
                </p>
                <p className='text-xs font-medium leading-4 text-[#5a5a5a]'>
                  {t(c.helperKey, { count: c.helperCount })}
                </p>
              </div>
            </div>
          </button>
        ))}
      </section>

      {/* Toolbar (Figma 8:1130): compact tag toggle, filters, search. */}
      <section className='shrink-0'>
        <div className='flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between'>
          <div className='flex min-w-0 flex-1 flex-wrap items-center gap-3'>
            <div className='inline-flex shrink-0 rounded-xl border border-[#e7e9ef] bg-white p-1 shadow-[0_1px_4px_rgba(38,45,66,0.05)]'>
              <button
                type='button'
                onClick={() => setView('list')}
                className={cn(
                  'inline-flex h-8 items-center gap-2 rounded-lg px-3 text-sm font-medium leading-none transition-colors',
                  view === 'list'
                    ? 'bg-[#fff1ea] text-primary'
                    : 'text-[#3d434e] hover:bg-accent'
                )}
              >
                <ListIcon className='h-4 w-4' />
                {t('list.viewList')}
              </button>
              <button
                type='button'
                onClick={() => setView('card')}
                className={cn(
                  'inline-flex h-8 items-center gap-2 rounded-lg px-3 text-sm font-medium leading-none transition-colors',
                  view === 'card'
                    ? 'bg-[#fff1ea] text-primary'
                    : 'text-[#3d434e] hover:bg-accent'
                )}
              >
                <LayoutGrid className='h-4 w-4' />
                {t('list.viewCard')}
              </button>
            </div>

            <DateRangeFilter
              appearance='field'
              labelKey='tabFilters.period'
              from={filters.due_date_from}
              to={filters.due_date_to}
              triggerClassName={FORM_CONTROL_FOCUS_CLASS}
              onChange={({ date_from, date_to }) =>
                setFilters({
                  due_date_from: date_from,
                  due_date_to: date_to,
                  overdue: undefined,
                })
              }
            />

            <MultiSelectFilter
              options={STATUS_OPTIONS.map(o => ({
                value: o.value,
                label: t(o.labelKey),
              }))}
              selected={selectedStatuses}
              onChange={values =>
                setFilters({
                  status: toCsv(values),
                  overdue: undefined,
                })
              }
              placeholder={t('list.filters.statusPlaceholder')}
              selectedLabel={count =>
                t('list.filters.statusSelected', { count })
              }
              clearLabel={t('list.filters.allStatuses')}
              emptyLabel={t('list.filters.statusEmpty')}
              triggerClass={cn(filterTriggerClass, FORM_CONTROL_FOCUS_CLASS)}
            />

            <Select
              value={filters.priority ?? ALL}
              onValueChange={v =>
                setFilters({ priority: v === ALL ? undefined : v })
              }
            >
              <SelectTrigger
                className={cn(filterTriggerClass, FORM_FILTER_HOVER_CLASS)}
              >
                <SelectValue
                  placeholder={t('list.filters.priorityPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>
                  {t('list.filters.allPriorities')}
                </SelectItem>
                {PRIORITY_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    {t(o.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className='order-last w-full xl:order-none xl:w-auto'>
              <div className='relative w-full xl:w-[320px]'>
                <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#6b7280]' />
                <Input
                  value={search}
                  onChange={e => onSearchChange(e.target.value)}
                  placeholder={t('list.filters.searchPlaceholder')}
                  maxLength={100}
                  className='h-10 w-full rounded-xl border-[#e7e9ef] bg-white pl-10 pr-4 text-sm font-normal placeholder:font-normal placeholder:text-[#6b7280]'
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {view === 'list' ? (
        <Card className='overflow-hidden rounded-2xl border border-[#e7e9ef] shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_rgba(38,45,66,0.1)]'>
          <div className='overflow-x-auto'>
            <table
              className='w-full table-fixed caption-bottom text-sm'
              style={{ minWidth: TABLE_MIN_WIDTH }}
            >
              <TableHeader className='sticky top-0 z-10 bg-[#fbfbfc]'>
                <TableRow className='border-0'>
                  <TableHead className={cn(thClass, COL.taskTitle)}>
                    {t('list.table.taskTitle')}
                  </TableHead>
                  <TableHead className={cn(thClass, COL.priority)}>
                    {t('list.table.priority')}
                  </TableHead>
                  <TableHead className={cn(thClass, COL.dueDate)}>
                    {t('list.table.dueDate')}
                  </TableHead>
                  <TableHead className={cn(thClass, COL.status)}>
                    {t('list.table.status')}
                  </TableHead>
                  <TableHead className={cn(thClass, COL.assignedTo)}>
                    {t('list.table.assignedTo')}
                  </TableHead>
                  <TableHead className={cn(thClass, COL.actions)}>
                    <div className='ml-auto text-right'>
                      {t('list.table.actions')}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                        <Skeleton className='h-6 w-full' />
                      </TableCell>
                    </TableRow>
                  ))}

                {isError && !isLoading && (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={cn(
                        tdClass,
                        'py-10 text-center text-destructive'
                      )}
                    >
                      {t('list.loadError')}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && !isError && tasks.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={cn(
                        tdClass,
                        'py-10 text-center text-muted-foreground'
                      )}
                    >
                      {t('list.empty')}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading &&
                  !isError &&
                  tasks.map(task => {
                    const linked = linkedEntity(task)
                    const overdue = isOverdue(task)
                    const callTooltip = task.assignee_name
                      ? t('list.callNamedAction', {
                          name: task.assignee_name,
                        })
                      : t('list.callAction')
                    return (
                      <TableRow
                        key={task.id}
                        className='group cursor-pointer transition-colors'
                        onClick={() => openTaskDetail(task.id)}
                      >
                        <TableCell
                          className={cn(tdClass, COL.taskTitle)}
                          style={{
                            borderLeft: `4px solid ${PRIORITY_ACCENT[task.priority]}`,
                          }}
                        >
                          <div className='space-y-1'>
                            <TruncatedTextTooltip
                              className={cn(
                                'line-clamp-2 text-sm font-medium leading-[1.4] text-foreground',
                                linked && 'text-primary hover:underline',
                                task.status === 'done' &&
                                  'text-muted-foreground line-through'
                              )}
                              content={task.title}
                            >
                              {task.title}
                            </TruncatedTextTooltip>
                            {linked && (
                              <div className='inline-flex items-center gap-1 text-xs text-muted-foreground'>
                                <ArrowUpRight className='h-3 w-3' />
                                {linked.label}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className={cn(tdClass, COL.priority)}>
                          <TaskPriorityBadge priority={task.priority} />
                        </TableCell>
                        <TableCell className={cn(tdClass, COL.dueDate)}>
                          <div className='flex items-center gap-2'>
                            <span
                              className={cn(
                                'text-sm leading-[1.4]',
                                overdue ? 'text-[#5a5a5a]' : 'text-foreground'
                              )}
                            >
                              {formatDate(task.due_date)}
                            </span>
                            {overdue && (
                              <span className={overdueBadgeClass}>
                                {t('list.overdue')}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className={cn(tdClass, COL.status)}>
                          <TaskStatusBadge status={task.status} />
                        </TableCell>
                        <TableCell className={cn(tdClass, COL.assignedTo)}>
                          {task.assignee_name || task.assigned_to ? (
                            <TruncatedTextTooltip
                              className='line-clamp-2 text-sm font-medium leading-[1.4] text-foreground'
                              content={
                                task.assignee_name ?? task.assigned_to ?? ''
                              }
                            >
                              {task.assignee_name ??
                                task.assigned_to!.slice(0, 8)}
                            </TruncatedTextTooltip>
                          ) : (
                            <span className='text-sm text-muted-foreground'>
                              {t('list.unassigned')}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className={cn(tdClass, COL.actions)}>
                          <div className={actionClusterClass}>
                            <TruncatedTextTooltip
                              content={callTooltip}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={actionBtnClass}
                                aria-label={callTooltip}
                                onClick={e => {
                                  e.stopPropagation()
                                  onCall(task)
                                }}
                              >
                                <Phone className={actionIconClass} />
                              </button>
                            </TruncatedTextTooltip>
                            <TruncatedTextTooltip
                              content={t('list.whatsappAction')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={cn(
                                  actionBtnClass,
                                  whatsappActionBtnClass
                                )}
                                aria-label={t('list.whatsappAction')}
                                onClick={e => {
                                  e.stopPropagation()
                                  onWhatsapp(task)
                                }}
                              >
                                <img
                                  src={whatsappLineIcon}
                                  alt=''
                                  width={16}
                                  height={16}
                                  className='size-4 shrink-0 transition-[filter] group-hover/whatsapp:[filter:brightness(0)_saturate(100%)_invert(55%)_sepia(74%)_saturate(1027%)_hue-rotate(118deg)_brightness(94%)_contrast(87%)]'
                                />
                              </button>
                            </TruncatedTextTooltip>
                            <Permit module='tasks' action='edit'>
                              {canUpdateTaskStatus(task, viewer) && (
                                <TruncatedTextTooltip
                                  content={t('list.updateStatusAction')}
                                  delayDuration={0}
                                  alwaysShow
                                  closeOnPointerDown
                                  className='inline-flex shrink-0'
                                >
                                  <button
                                    type='button'
                                    className={actionBtnClass}
                                    aria-label={t('list.updateStatusAction')}
                                    onClick={e => {
                                      e.stopPropagation()
                                      setStatusTask(task)
                                    }}
                                  >
                                    <RefreshCw className={actionIconClass} />
                                  </button>
                                </TruncatedTextTooltip>
                              )}
                            </Permit>
                            <TruncatedTextTooltip
                              content={t('list.viewAction')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={actionBtnClass}
                                aria-label={t('list.viewAction')}
                                onClick={e => {
                                  e.stopPropagation()
                                  openTaskDetail(task.id)
                                }}
                              >
                                <Eye className={actionIconClass} />
                              </button>
                            </TruncatedTextTooltip>
                            <Permit module='tasks' action='delete'>
                              <TruncatedTextTooltip
                                content={t('list.deleteAction')}
                                delayDuration={0}
                                alwaysShow
                                closeOnPointerDown
                                className='inline-flex shrink-0'
                              >
                                <button
                                  type='button'
                                  className={cn(
                                    actionBtnClass,
                                    destructiveActionBtnClass
                                  )}
                                  aria-label={t('list.deleteAction')}
                                  onClick={e => {
                                    e.stopPropagation()
                                    setToDelete(task)
                                  }}
                                >
                                  <Trash2 className={actionIconClass} />
                                </button>
                              </TruncatedTextTooltip>
                            </Permit>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </table>
          </div>

          <ListPagination
            className='border-t border-border bg-background'
            metadata={data?.metadata}
            page={page}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
            showSummary={false}
          />
        </Card>
      ) : (
        <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
          <div className='flex min-h-0 flex-1 gap-4 overflow-x-auto pb-2'>
            {KANBAN_COLUMNS.map(col => {
              const colQuery = kanbanQueries[col.status]
              const colTasks =
                colQuery.data?.pages.flatMap(page => page.data) ?? []
              const colTotal =
                colQuery.data?.pages[0]?.metadata?.total ?? colTasks.length
              const theme = KANBAN_COLUMN_THEMES[col.status]
              return (
                <div
                  key={col.status}
                  className='flex min-h-0 min-w-[320px] flex-1 flex-col gap-2 overflow-hidden rounded-3xl bg-[#E7E9EF] xl:min-w-0'
                >
                  <div className='flex shrink-0 items-center justify-between px-3 pt-3'>
                    <div className='inline-flex items-center gap-2'>
                      <span
                        className='h-2 w-2 rounded-full'
                        style={{ backgroundColor: theme.dot }}
                      />
                      <span
                        className='text-xs font-medium uppercase leading-4 tracking-normal text-[#3d434e]'
                        style={{
                          color: theme.label,
                        }}
                      >
                        {t(col.labelKey)}
                      </span>
                    </div>
                    <span
                      className='inline-flex h-6 min-w-6 items-center justify-center rounded-lg px-2 py-1 text-xs font-medium leading-4'
                      style={{
                        backgroundColor: theme.countBg,
                        color: theme.countText,
                      }}
                    >
                      {formatInteger(colTotal)}
                    </span>
                  </div>

                  <div
                    className='flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 pb-3 pt-1 overscroll-y-contain'
                    onScroll={event => {
                      if (!shouldLoadMoreKanbanTasks(event.currentTarget))
                        return
                      if (!colQuery.hasNextPage || colQuery.isFetchingNextPage)
                        return
                      void colQuery.fetchNextPage()
                    }}
                  >
                    {colQuery.isLoading &&
                      Array.from({ length: 2 }).map((_, i) => (
                        <Skeleton
                          key={i}
                          className='h-[140px] w-full shrink-0 rounded-2xl'
                        />
                      ))}

                    {colQuery.isError && !colQuery.isLoading && (
                      <p className='shrink-0 rounded-2xl border border-dashed border-[#f8b4b4] bg-white py-10 text-center text-xs text-destructive shadow-[0_1px_4px_-1px_rgba(38,45,66,0.05)]'>
                        {t('list.loadError')}
                      </p>
                    )}

                    {!colQuery.isLoading &&
                      !colQuery.isError &&
                      colTasks.length === 0 && (
                        <p className='shrink-0 rounded-2xl border border-dashed border-[#d7dbe5] bg-white py-10 text-center text-xs text-muted-foreground shadow-[0_1px_4px_-1px_rgba(38,45,66,0.05)]'>
                          {t('list.noTasks')}
                        </p>
                      )}

                    {!colQuery.isLoading &&
                      !colQuery.isError &&
                      colTasks.map(task => {
                        const overdue = isOverdue(task)
                        const linked = linkedEntity(task)
                        const assigneeLabel =
                          task.assignee_name ??
                          (task.assigned_to
                            ? task.assigned_to.slice(0, 8)
                            : t('list.unassigned'))
                        const callTooltip = task.assignee_name
                          ? t('list.callNamedAction', {
                              name: task.assignee_name,
                            })
                          : t('list.callAction')
                        return (
                          <div
                            key={task.id}
                            className='flex shrink-0 cursor-pointer flex-col overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_-1px_rgba(38,45,66,0.05),0_1px_8px_-1px_rgba(38,45,66,0.1)] transition-shadow hover:shadow-[0_8px_18px_-10px_rgba(23,26,32,0.28)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                            onClick={() => openTaskDetail(task.id)}
                          >
                            <div className='flex flex-col gap-2 p-3'>
                              <div className='flex flex-col items-start gap-2 border-b border-[#e7e9ef] pb-3'>
                                <TaskPriorityBadge priority={task.priority} />
                                <button
                                  type='button'
                                  className='block w-full text-left text-sm font-medium leading-[1.4] text-[#171a20] hover:underline'
                                  onClick={e => {
                                    e.stopPropagation()
                                    openTaskDetail(task.id)
                                  }}
                                >
                                  {task.title}
                                </button>
                              </div>
                              <div className='flex flex-wrap items-center gap-1.5 text-xs font-medium leading-4 text-[#3d434e]'>
                                <p>{assigneeLabel}</p>
                                {task.due_date && (
                                  <>
                                    <span className='h-1 w-1 rounded-full bg-[#b8bfcc]' />
                                    <span className='inline-flex items-center gap-1'>
                                      <svg
                                        viewBox='0 0 16 16'
                                        className='h-[14px] w-[14px] text-[#3d434e]'
                                        fill='none'
                                        stroke='currentColor'
                                        strokeWidth='1.5'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                      >
                                        <rect
                                          x='2.5'
                                          y='3.5'
                                          width='11'
                                          height='10'
                                          rx='1.5'
                                        />
                                        <path d='M5 2.5V5M11 2.5V5M2.5 6.5H13.5' />
                                      </svg>
                                      {formatDate(task.due_date)}
                                    </span>
                                  </>
                                )}
                                {overdue && (
                                  <span className={kanbanOverdueTextClass}>
                                    {t('list.overdue')}
                                  </span>
                                )}
                                {linked && (
                                  <button
                                    type='button'
                                    className='inline-flex items-center gap-1 text-left text-xs font-medium leading-4 text-primary hover:underline'
                                    onClick={e => {
                                      e.stopPropagation()
                                      navigate(linked.to)
                                    }}
                                  >
                                    <ArrowUpRight className='size-[14px]' />
                                    {linked.label}
                                  </button>
                                )}
                              </div>
                            </div>

                            <div className='flex items-center justify-between px-3 pb-3'>
                              <TruncatedTextTooltip
                                content={callTooltip}
                                delayDuration={0}
                                alwaysShow
                                closeOnPointerDown
                                className='inline-flex shrink-0'
                              >
                                <button
                                  type='button'
                                  className={cardActionBtnClass}
                                  aria-label={callTooltip}
                                  onClick={e => {
                                    e.stopPropagation()
                                    onCall(task)
                                  }}
                                >
                                  <Phone className={cardActionIconClass} />
                                </button>
                              </TruncatedTextTooltip>
                              <TruncatedTextTooltip
                                content={t('list.whatsappAction')}
                                delayDuration={0}
                                alwaysShow
                                closeOnPointerDown
                                className='inline-flex shrink-0'
                              >
                                <button
                                  type='button'
                                  className={cn(
                                    cardActionBtnClass,
                                    cardWhatsappActionBtnClass
                                  )}
                                  aria-label={t('list.whatsappAction')}
                                  onClick={e => {
                                    e.stopPropagation()
                                    onWhatsapp(task)
                                  }}
                                >
                                  <img
                                    src={whatsappLineIcon}
                                    alt=''
                                    width={16}
                                    height={16}
                                    className='size-4 shrink-0 transition-[filter] group-hover/card-whatsapp:[filter:brightness(0)_saturate(100%)_invert(55%)_sepia(74%)_saturate(1027%)_hue-rotate(118deg)_brightness(94%)_contrast(87%)]'
                                  />
                                </button>
                              </TruncatedTextTooltip>
                              <Permit module='tasks' action='edit'>
                                {canUpdateTaskStatus(task, viewer) && (
                                  <TruncatedTextTooltip
                                    content={t('list.updateStatusAction')}
                                    delayDuration={0}
                                    alwaysShow
                                    closeOnPointerDown
                                    className='inline-flex shrink-0'
                                  >
                                    <button
                                      type='button'
                                      className={cardActionBtnClass}
                                      aria-label={t('list.updateStatusAction')}
                                      onClick={e => {
                                        e.stopPropagation()
                                        setStatusTask(task)
                                      }}
                                    >
                                      <RefreshCw
                                        className={cardActionIconClass}
                                      />
                                    </button>
                                  </TruncatedTextTooltip>
                                )}
                              </Permit>
                              <TruncatedTextTooltip
                                content={t('list.viewAction')}
                                delayDuration={0}
                                alwaysShow
                                closeOnPointerDown
                                className='inline-flex shrink-0'
                              >
                                <button
                                  type='button'
                                  className={cardActionBtnClass}
                                  aria-label={t('list.viewAction')}
                                  onClick={e => {
                                    e.stopPropagation()
                                    openTaskDetail(task.id)
                                  }}
                                >
                                  <Eye className={cardActionIconClass} />
                                </button>
                              </TruncatedTextTooltip>
                              <Permit module='tasks' action='delete'>
                                <TruncatedTextTooltip
                                  content={t('list.deleteAction')}
                                  delayDuration={0}
                                  alwaysShow
                                  closeOnPointerDown
                                  className='inline-flex shrink-0'
                                >
                                  <button
                                    type='button'
                                    className={cn(
                                      cardActionBtnClass,
                                      cardDestructiveActionBtnClass
                                    )}
                                    aria-label={t('list.deleteAction')}
                                    onClick={e => {
                                      e.stopPropagation()
                                      setToDelete(task)
                                    }}
                                  >
                                    <Trash2 className={cardActionIconClass} />
                                  </button>
                                </TruncatedTextTooltip>
                              </Permit>
                            </div>
                          </div>
                        )
                      })}

                    {!colQuery.isLoading && colQuery.isFetchingNextPage && (
                      <div
                        role='status'
                        aria-label={t('list.loadingMore')}
                        className='flex shrink-0 items-center justify-center py-3 text-primary'
                      >
                        <Loader2 className='h-5 w-5 animate-spin' />
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <PaymentDrawerShell
        open={drawerMode !== null}
        title={
          drawerMode === 'detail'
            ? t('detail.drawerTitle')
            : drawerMode === 'edit'
              ? t('form.editTitle')
              : t('form.createTitle')
        }
        onClose={closePanel}
        titleClassName='text-base font-semibold'
      >
        {drawerMode === 'detail' && detailTaskId ? (
          <TaskDetailDrawerContent
            key={`detail-${detailTaskId}`}
            taskId={detailTaskId}
            onClose={closePanel}
          />
        ) : drawerMode === 'edit' && editingTaskId ? (
          <TaskFormPage
            key={`edit-${editingTaskId}`}
            mode='edit'
            embedded
            onCancelOverride={closePanel}
          />
        ) : drawerMode === 'create' ? (
          <TaskFormPage
            key='create'
            mode='create'
            embedded
            onCancelOverride={closePanel}
          />
        ) : null}
      </PaymentDrawerShell>

      <DeleteTaskDialog
        task={toDelete}
        onClose={() => setToDelete(null)}
        onDeleted={() => setToDelete(null)}
      />

      {statusTask && (
        <UpdateTaskStatusDialog
          taskId={statusTask.id}
          current={statusTask.status}
          open={!!statusTask}
          onClose={() => setStatusTask(null)}
        />
      )}
    </div>
  )
}
